/**
 * Hook for accessing the current user's role and permissions.
 *
 * Uses the trpc.users.me query to get the role from the database,
 * which is more reliable than parsing the session cookie.
 */

import { trpc } from '@/trpc/client'

export type AppRole = 'USER' | 'PRESENTER' | 'ADMIN'

const ROLE_LEVEL: Record<AppRole, number> = {
	USER: 1,
	PRESENTER: 2,
	ADMIN: 3,
}

function normalizeRole(role: string | null | undefined): AppRole {
	if (role === 'ADMIN' || role === 'PRESENTER' || role === 'USER') {
		return role
	}
	return 'USER'
}

export function useUserRole() {
	const { data: user, isLoading, error } = trpc.users.me.useQuery(undefined, {
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		retry: false,
	})

	const role = normalizeRole(user?.role)

	return {
		role,
		isAdmin: role === 'ADMIN',
		isPresenter: ROLE_LEVEL[role] >= ROLE_LEVEL.PRESENTER,
		isUser: role === 'USER',
		isLoading,
		error,
	}
}
